import Layout from '@components/Layout';
import { ReactElement } from 'react';
import { pageHeaderStyle, SmallPageHeader } from '@components/Text';
import { ContentPadding, FullBleedPadding } from '@components/ContentPadding';
import { ButtonLink, TextLink } from '@components/Link';
import { teamList } from '@utils/teamList';
import Twitter from '../icon/twitter.svg';
import { gql } from '@apollo/client';
import { GetStaticPropsContext } from 'next';
import { getClient } from 'db/contentAPI';
import { GetAboutPageQuery } from 'generated/graphql';
import FrequentlyAskedQuestions from '@components/FrequentlyAskedQuestions';
import { BuyObscuraMintPass3 } from '@components/BuyButtons';
import { COMMISSIONS } from 'logic/commissions';
import { MarkdownText } from '@components/MarkdownText';
import { useQueryWithFallback } from 'hooks/useDataWithFallback';

export default function AboutPage({
  data,
}: {
  data: GetAboutPageQuery;
}): ReactElement {
  const page = useQueryWithFallback(queryAboutPage, data);
  const styles = {
    projectListItem: 'my-4 font-semibold text-xl',
    paragraphStyle: 'mb-4 mx-auto w-3/4 flex-wrap text-lg',
  };

  return (
    <Layout>
      <ContentPadding className="mb-20">
        <SmallPageHeader className={pageHeaderStyle}>
          <MarkdownText text={page.about?.headline} />
        </SmallPageHeader>
        <p className={styles.paragraphStyle}>
          <MarkdownText text={page.about.cta1SubCopy} />
        </p>
      </ContentPadding>
      <ContentPadding className=" bg-black py-20 text-white">
        <p className="text-white font-semibold  text-5xl">
          <MarkdownText text={page.about.collectionLinksCta} />
        </p>
        <ul className={`${styles.paragraphStyle} pt-10 `}>
          {page.about.collectionLinksCollection.items.map((link) => (
            <TextLink key={link.label} href={link.url} className="no-underline">
              <li className={styles.projectListItem}>• {link.label}</li>
            </TextLink>
          ))}
        </ul>
      </ContentPadding>
      <FrequentlyAskedQuestions faq={page.about.faqCollection.items} />
      <FullBleedPadding className="mt-12">
        <div className="container mx-auto">
          <div className="flex justify-around">
            <div className="rounded-2xl text-center md:w-2/3">
              <h2 className="text-center capitalize">Founders</h2>
              <div>
                {teamList.map(({ name, description, twitter, img }, i) => {
                  return (
                    <div key={i}>
                      <div className="flex flex-none my-12">
                        <div
                          className="text-left mr-8"
                          style={{ minWidth: '5rem', width: '5rem' }}
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            className="rounded-full"
                            alt=""
                            src={'./' + img}
                          />
                        </div>
                        <div>
                          <div className="flex">
                            <p className="text-left text-black text-xl sm:text-2xl font-medium mb-1">
                              {name}
                            </p>
                            <a
                              href={'https://twitter.com/' + twitter}
                              className="self-center"
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Twitter className="fill-current text-black w-5 h-5 mx-3 hover:opacity-80" />
                            </a>
                          </div>
                          <p className="text-left text-black text-l sm:text-xl">
                            {description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </FullBleedPadding>
    </Layout>
  );
}

const queryAboutPage = gql`
  query GetAboutPage($preview: Boolean) {
    about(id: "2QTcmiocnFnoYThe4WMjU2", preview: $preview) {
      headline
      cta1SubCopy
      collectionLinksCta
      collectionLinksCollection {
        items {
          label
          url
        }
      }
      cta3
      faqCollection {
        items {
          question
          answer
        }
      }
    }
  }
`;

export async function getStaticProps({
  preview,
  previewData,
}: GetStaticPropsContext) {
  const { data, error } = await getClient(
    (previewData as { api: string })?.api
  ).query({
    query: queryAboutPage,
    variables: {
      preview,
    },
  });

  if (error) {
    console.error(`Failed to fetch about, received ${JSON.stringify(error)}`);
  }

  return {
    props: {
      data,
      fallback: true,
      preview: preview ?? false,
      revalidate: 120,
    },
  };
}
